/**
 * 前端图片压缩工具
 *
 * 使用 Canvas API 将用户上传的图片压缩为 JPEG，减少传输体积。
 * 仅用于 img2img 模式的参考图压缩。
 */

export interface CompressOptions {
  /** 最大宽度，默认 2048 */
  maxWidth?: number;
  /** 最大高度，默认 2048 */
  maxHeight?: number;
  /** JPEG 质量 0-1，默认 0.8 */
  quality?: number;
}

const DEFAULT_MAX_WIDTH = 2048;
const DEFAULT_MAX_HEIGHT = 2048;
const DEFAULT_QUALITY = 0.8;

/**
 * 压缩单张图片文件，返回 JPEG data URL。
 *
 * - 保持宽高比缩放，不放大小图
 * - 输出统一为 JPEG 格式
 * - 压缩失败时返回 null（调用方应 fallback 到原始 FileReader）
 */
export async function compressImageFile(
  file: File,
  options: CompressOptions = {}
): Promise<string | null> {
  const {
    maxWidth = DEFAULT_MAX_WIDTH,
    maxHeight = DEFAULT_MAX_HEIGHT,
    quality = DEFAULT_QUALITY,
  } = options;

  try {
    // 使用 createImageBitmap 解码图片（比 new Image() 更高效，不阻塞主线程）
    const bitmap = await createImageBitmap(file);

    let targetWidth = bitmap.width;
    let targetHeight = bitmap.height;

    // 计算缩放比例（保持宽高比，不放大）
    if (targetWidth > maxWidth || targetHeight > maxHeight) {
      const ratio = Math.min(maxWidth / targetWidth, maxHeight / targetHeight);
      targetWidth = Math.round(targetWidth * ratio);
      targetHeight = Math.round(targetHeight * ratio);
    }

    // 使用 OffscreenCanvas（如果可用）或普通 Canvas
    const canvas = document.createElement('canvas');
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) {
      bitmap.close();
      return null;
    }

    // 白色背景（处理 PNG 透明通道）
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);

    // 绘制图片
    ctx.drawImage(bitmap, 0, 0, targetWidth, targetHeight);
    bitmap.close();

    // 导出为 JPEG data URL
    const dataUrl = canvas.toDataURL('image/jpeg', quality);

    // 简单校验：如果输出异常小（可能是空白图），返回 null 让调用方 fallback
    if (dataUrl.length < 100) {
      return null;
    }

    return dataUrl;
  } catch {
    // 压缩失败（不支持的格式等），返回 null
    return null;
  }
}

/**
 * 批量压缩图片文件。
 * 对每个文件尝试压缩，失败的文件 fallback 到原始 FileReader 读取。
 */
export async function compressImageFiles(
  files: File[],
  options: CompressOptions = {}
): Promise<string[]> {
  const results = await Promise.all(
    files.map(async (file) => {
      const compressed = await compressImageFile(file, options);
      if (compressed) return compressed;

      // fallback：原始 FileReader
      return new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = (event) => {
          resolve((event.target?.result as string) || '');
        };
        reader.onerror = () => resolve('');
        reader.readAsDataURL(file);
      });
    })
  );

  // 过滤掉空字符串（理论上不会出现）
  return results.filter(Boolean);
}
